/**
 * List of selectable options for enum/set/people profile field pickers.
 * Uses React Native primitives on all platforms — @expo/ui List has unreliable
 * layout/scrolling inside stack screens on iOS.
 */
import { ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import type { SelectOption } from "@/components/ui/enum-select";
import { useTranslation } from "react-i18next";
import { Settings, SettingsItem } from "./settings";
import { Text } from "./text";

export type SelectOptionListProps = {
    options: SelectOption[];
    isSelected: (optionId: number) => boolean;
    onOptionPress: (option: SelectOption) => void;
    multiple?: boolean;
    description?: string;
};

export function SelectOptionList({
    options,
    isSelected,
    onOptionPress,
    multiple,
    description,
}: SelectOptionListProps) {
    const { t } = useTranslation();
    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            <ScrollView
                className="flex-1"
                contentContainerClassName="gap-2 px-4 pb-6 pt-2 web:mx-auto web:w-full web:max-w-lg"
                keyboardShouldPersistTaps="handled"
            >
                {description && (
                    <View className="rounded-lg p-4 border border-border bg-card mb-2">
                        <Text variant="caption">{description}</Text>
                    </View>
                )}
                <Text variant="caption" className="ml-2">
                    {multiple
                        ? t("ui.selectOneOrMoreOptions")
                        : t("ui.selectOneOption")}
                </Text>
                <Settings>
                    {options.map((option, index) => (
                        <SettingsItem
                            key={option.id}
                            label={option.label}
                            onPress={() => onOptionPress(option)}
                            selected={isSelected(option.id)}
                        ></SettingsItem>
                    ))}
                </Settings>
            </ScrollView>
        </SafeAreaView>
    );
}
